<script setup lang="ts">
import {
  PhDotsSixVertical,
  PhFilePdf,
  PhTrash,
  PhWarning,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';
import type { PdfMergeErrorReason } from './usePdfMerge';

defineProps<{
  id: string;
  fileName: string;
  pageCount?: number;
  errorReason?: PdfMergeErrorReason;
}>();

defineEmits<{ remove: [id: string] }>();

const { t } = useI18n();
</script>

<template>
  <li class="pdf-merger-row" :class="{ 'is-error': !!errorReason }">
    <button
      type="button"
      class="pdf-merger-row__handle"
      :aria-label="t('pdfMerger.reorderHint')"
    >
      <PhDotsSixVertical :size="18" />
    </button>

    <PhFilePdf :size="20" class="pdf-merger-row__icon" />

    <div class="pdf-merger-row__info">
      <span class="pdf-merger-row__name" :title="fileName">{{ fileName }}</span>
      <span v-if="errorReason" class="pdf-merger-row__error">
        <PhWarning :size="13" class="mr-1" />
        {{ t(`pdfMerger.errors.${errorReason}`) }}
      </span>
      <span v-else-if="pageCount !== undefined" class="pdf-merger-row__pages">
        {{ t('pdfMerger.pageCount', pageCount) }}
      </span>
    </div>

    <button
      type="button"
      class="pdf-merger-row__remove"
      :aria-label="t('pdfMerger.remove')"
      @click="$emit('remove', id)"
    >
      <PhTrash :size="16" />
    </button>
  </li>
</template>

<style scoped>
.pdf-merger-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  background: #fff;
  /* Hold the row to the container width so a long file name truncates rather
     than widening the item. */
  width: 100%;
  min-width: 0;
}

.pdf-merger-row.is-dragging {
  opacity: 0.6;
}

.pdf-merger-row.is-error {
  border-color: #f14668;
  background: #feecf0;
}

.pdf-merger-row__handle {
  cursor: grab;
  border: none;
  background: none;
  padding: 0;
  color: #7a7a7a;
  display: flex;
  touch-action: none;
}

.pdf-merger-row__handle:active {
  cursor: grabbing;
}

.pdf-merger-row__icon {
  color: #f14668;
  flex-shrink: 0;
}

.pdf-merger-row__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.pdf-merger-row__name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pdf-merger-row__pages {
  font-size: 0.75rem;
  color: #7a7a7a;
}

.pdf-merger-row__error {
  font-size: 0.75rem;
  color: #cc0f35;
  display: flex;
  align-items: center;
}

.pdf-merger-row__remove {
  border: none;
  background: none;
  cursor: pointer;
  color: #7a7a7a;
  display: flex;
  flex-shrink: 0;
}

.pdf-merger-row__remove:hover {
  color: #f14668;
}
</style>
